import React, { Suspense, useEffect, useMemo } from 'react';

import { loadEntryPoint, useRelayEnvironment } from 'react-relay';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { expVal } from '@atlaskit/platform-feature-experiments/exp-val';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { AgentProfileCardResourcedProps } from '@atlaskit/profilecard/agent-profile-card-resourced';
import { agentProfileCardEntryPoint as legacyAgentProfileCardEntryPoint } from '@atlassian/conversation-assistant-agent/common/ui/agent-profile-card-entrypoint';
import type { AgentProfileCardRuntimeProps } from '@atlassian/conversation-assistant-agent/common/ui/agent-profile-card-types';
import { InternalEntryPointContainer } from '@atlassian/internal-entry-point-container/ui';
import type { AgentCardRelayProps } from '@atlassian/studio-browse-kit/agent-card-relay';

import { AgentCardLoading } from './AgentCardLoading';
import { agentProfileCardEntryPoint } from './agentProfileCard.entrypoint';
import { AgentProfileCardCreditsWrapper } from './components/AgentProfileCardCreditsWrapper';

export type AgentCardProps = AgentProfileCardResourcedProps &
	Omit<
		AgentCardRelayRuntimeProps,
		'cloudId' | 'identityAccountId' | 'onConversationStarterClick' | 'onDelete'
	>;

export type AgentCardSlots = Pick<NonNullable<AgentCardRelayProps['slots']>, 'content' | 'footer'>;

export type AgentCardRelayRuntimeProps = AgentProfileCardRuntimeProps & {
	/** Legacy cards only. The browse-kit card ignores this prop; use slots.footer. */
	renderCTA?: AgentProfileCardRuntimeProps['renderCTA'];
	slots?: AgentCardSlots;
};

function AgentCardRelay({
	accountId,
	cloudId,
	hideStarButton,
	hideConversationStarters,
	hideAiDisclaimer,
	onChatClick,
	onConversationStartersClick,
	onViewProfile,
	onDuplicate,
	onCopyLink,
	onEdit,
	onDeleteAgent,
	renderCTA,
	slots,
	renderMoreActions,
}: AgentCardProps): React.JSX.Element {
	const environment = useRelayEnvironment();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isBrowseKitEnabled =
		fg('rovo_agent_profile_card_browse_kit') &&
		expVal('jira_browse_agent_modal_update', 'isEnabled', false);
	const entryPoint = isBrowseKitEnabled
		? agentProfileCardEntryPoint
		: legacyAgentProfileCardEntryPoint;
	const fallback = <AgentCardLoading />;

	const entryPointReference = useMemo(
		() =>
			loadEntryPoint({ getEnvironment: () => environment }, entryPoint, {
				identityAccountId: accountId,
				cloudId,
			}),
		[accountId, cloudId, environment, entryPoint],
	);

	const handleConversationStarterClick = useMemo(
		() => (prompt: string) => onConversationStartersClick?.({ message: prompt, type: 'static' }),
		[onConversationStartersClick],
	);

	const handleDelete = useMemo(
		() =>
			onDeleteAgent
				? (agentId: string) => {
						onDeleteAgent(agentId);
					}
				: undefined,
		[onDeleteAgent],
	);

	useEffect(() => {
		createAnalyticsEvent({
			action: 'mounted',
			actionSubject: 'agentCardRelay',
			eventType: 'operational',
			attributes: {
				source: 'agentCard',
			},
		}).fire();
		return () => {
			entryPointReference.dispose?.();
		};
	}, [entryPointReference, createAnalyticsEvent]);

	const card = (
		<Suspense fallback={fallback}>
			<InternalEntryPointContainer
				id="agent-profile-card"
				entryPointReference={entryPointReference}
				runtimeProps={{
					cloudId,
					identityAccountId: accountId,
					hideStarButton,
					hideConversationStarters,
					hideAiDisclaimer,
					onChatClick,
					onConversationStarterClick: handleConversationStarterClick,
					onViewProfile,
					onDuplicate,
					onCopyLink,
					onEdit,
					onDelete: handleDelete,
					renderCTA,
					slots,
					renderMoreActions,
				}}
				fallback={fallback}
			/>
		</Suspense>
	);
	return isBrowseKitEnabled ? (
		<AgentProfileCardCreditsWrapper cloudId={cloudId}>{card}</AgentProfileCardCreditsWrapper>
	) : (
		card
	);
}

/**
 * Profile card for a Rovo agent principal.
 *
 * Uses the Relay entrypoint to load the agent profile card.
 */
export function AgentCard(props: AgentCardProps): React.JSX.Element {
	return <AgentCardRelay {...props} />;
}
